import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Card } from './common/Card';
import { Button } from './common/Button';

import { logger, getErrorMessage } from '../utils/logger';
/**
 * ErrorBoundary Component (Enhanced Production-Grade)
 * 
 * React Error Boundary that catches component rendering errors.
 * 
 * Features:
 * - Error recovery (retry/reset)
 * - Error reporting (console + future Sentry integration)
 * - Fallback UI with Card/Button components
 * - Development vs production modes
 * - Error context preservation (component stack)
 * - User-friendly error messages
 * - Dark mode compatible
 * - Mobile-first responsive
 * 
 * What it catches:
 * - Component rendering errors
 * - Lifecycle method errors
 * - Constructor errors
 * 
 * What it does NOT catch:
 * - Event handlers (use try-catch)
 * - Async code (use try-catch)
 * - Server-side rendering errors
 * - Errors in error boundary itself
 * 
 * Industry Pattern: Sentry ErrorBoundary, React Error Boundary best practices
 * 
 * @component
 * @example
 * ```tsx
 * // Wrap your entire app
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * 
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomError />}>
 *   <SomeComponent />
 * </ErrorBoundary>
 * 
 * // With error callback
 * <ErrorBoundary onError={(error, errorInfo) => {
 *   logErrorToService(error, errorInfo);
 * }}>
 *   <App />
 * </ErrorBoundary>
 * ```
 * 
 * @param {ErrorBoundaryProps} props - Component props
 * @param {ReactNode} props.children - Child components to wrap
 * @param {ReactNode} [props.fallback] - Custom fallback UI
 * @param {Function} [props.onError] - Error callback function
 * @param {Function} [props.onReset] - Reset callback function
 * 
 * @returns {JSX.Element} Error boundary wrapper
 */

export interface ErrorBoundaryProps {
  /** Child components */
  children: ReactNode;
  /** Custom fallback UI */
  fallback?: ReactNode;
  /** Error callback (for logging) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Reset callback (called after reset) */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  /** Has error occurred */
  hasError: boolean;
  /** Error object */
  error: Error | null;
  /** Error info (component stack) */
  errorInfo: ErrorInfo | null;
  /** Error count (for retry limiting) */
  errorCount: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  /**
   * Static method called when error occurs
   * Updates state to trigger fallback UI
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Called after error is caught
   * Logs error and calls onError callback
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Increment error count
    this.setState(prev => ({
      errorInfo,
      errorCount: prev.errorCount + 1,
    }));

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.error('[ErrorBoundary] Caught error:', { error: getErrorMessage(error) });
      logger.error('� [ErrorBoundary] Component stack:', { data: errorInfo.componentStack });
    }

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Future: Send to error reporting service (Sentry, Bugsnag, etc.)
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  /**
   * Reset error boundary state
   * Attempts to recover by re-rendering children
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call onReset callback if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  /**
   * Navigate to home page
   */
  handleGoHome = (): void => {
    window.location.href = '/';
  };

  /**
   * Reload the page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback } = this.props;

    // No error - render children
    if (!hasError) {
      return children;
    }

    // Custom fallback provided
    if (fallback) {
      return fallback;
    }

    // Default fallback UI
    const isDevelopment = process.env.NODE_ENV === 'development';
    const tooManyErrors = errorCount > 3;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
        <Card padding="lg" shadow="lg" className="max-w-2xl w-full">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-full text-red-600 dark:text-red-400">
              <AlertTriangle className="w-16 h-16" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 text-center mb-3">
            {tooManyErrors ? 'Persistent Error Detected' : 'Something Went Wrong'}
          </h1>

          {/* Description */}
          <p className="text-gray-600 dark:text-slate-400 text-center mb-6">
            {tooManyErrors
              ? 'The application encountered multiple errors. Please reload the page or contact support if the problem persists.'
              : 'The application encountered an unexpected error. You can try recovering or reloading the page.'}
          </p>

          {/* Error Details (Development Only) */}
          {isDevelopment && error && (
            <div className="mb-6 p-4 bg-gray-100 dark:bg-slate-800 rounded-lg border border-gray-300 dark:border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Bug className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                  Error Details (Development Only)
                </h3>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-gray-700 dark:text-slate-300">Message:</p>
                  <p className="text-xs text-red-600 dark:text-red-400 font-mono">{error.message}</p>
                </div>
                {error.stack && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 dark:text-slate-300">Stack Trace:</p>
                    <pre className="text-xs text-gray-600 dark:text-slate-400 font-mono overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {error.stack}
                    </pre>
                  </div>
                )}
                {errorInfo && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 dark:text-slate-300">Component Stack:</p>
                    <pre className="text-xs text-gray-600 dark:text-slate-400 font-mono overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                  Error count: {errorCount}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {!tooManyErrors && (
              <Button 
                variant="primary" 
                size="md"
                onClick={this.handleReset}
                icon={<RefreshCw className="w-4 h-4" />}
                iconPosition="left"
              >
                Try Again
              </Button>
            )}
            <Button 
              variant={tooManyErrors ? 'primary' : 'secondary'}
              size="md"
              onClick={this.handleReload}
              icon={<RefreshCw className="w-4 h-4" />}
              iconPosition="left"
            >
              Reload Page
            </Button>
            <Button 
              variant="secondary" 
              size="md"
              onClick={this.handleGoHome}
              icon={<Home className="w-4 h-4" />}
              iconPosition="left"
            >
              Go Home
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-xs text-gray-500 dark:text-slate-500 text-center mt-6">
            If this problem persists, please contact support with details about what you were doing when the error occurred.
          </p>
        </Card>
      </div>
    );
  }
}
